import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import VideoCard from "../components/VideoCard";
import { useAuth } from "../context/AuthContext";
import { askForPremium } from "../lib/askPremium";
import { usePlayer } from "../context/PlayerContext";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, Play, Sparkles, X, Lock } from "lucide-react";

export function PlaylistsIndex() {
  const { user } = useAuth();
  const [playlists, setPlaylists] = useState([]);
  const [name, setName] = useState("");
  const isStandard = user && !user.is_premium && user.role !== "admin";

  const load = () => api.get("/playlists").then((r) => setPlaylists(r.data));
  useEffect(() => { if (user) load(); }, [user]);

  if (!user) return (
    <div className="py-20 text-center">
      <h1 className="font-display text-3xl mb-3">Sign in to build playlists</h1>
      <Link to="/login" className="text-cyan-400 hover:underline">Log in</Link>
    </div>
  );

  const create = async (e) => {
    e.preventDefault();
    if (isStandard && playlists.length >= 1) {
      askForPremium("Free users can only have 1 playlist");
      return;
    }
    if (!name.trim()) return;
    try { await api.post("/playlists", { name }); setName(""); load(); toast.success("Playlist created"); }
    catch (err) {
      if (!["premium_required", "email_not_verified"].includes(err?.response?.data?.detail)) toast.error("Failed");
    }
  };

  return (
    <div>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold mb-6">Your Playlists</h1>
      <form onSubmit={create} className="flex gap-2 mb-8 max-w-md">
        <input data-testid="new-playlist-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="New playlist name"
          className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 focus:outline-none focus:border-cyan-400/50" />
        <button data-testid="create-playlist-btn" className="px-4 py-2 rounded-full bg-cyan-400 text-black font-semibold hover:bg-cyan-300 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Create
        </button>
      </form>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {playlists.map((p) => (
          <Link key={p.id} to={`/playlists/${p.id}`} data-testid={`playlist-${p.id}`} data-locked={p.locked ? "yes" : "no"}
            className={`bg-zinc-900/60 border border-white/10 rounded-xl p-5 card-hover block ${p.locked ? "opacity-50 grayscale" : ""}`}>
            <div className="font-display text-lg font-semibold flex items-center gap-2">{p.name}{p.locked && <Lock className="w-4 h-4 text-amber-400 shrink-0" />}</div>
            <div className="text-xs text-zinc-500 font-mono-ui uppercase mt-1">{p.item_count} track{p.item_count === 1 ? "" : "s"}</div>
            {p.locked && <div className="text-xs text-amber-300 mt-2">Saved. Premium unlocks it again.</div>}
            {!p.locked && isStandard && p.item_count > 10 && <div className="text-xs text-amber-300 mt-2">Free accounts play the first 10 songs. Premium unlocks the rest.</div>}
          </Link>
        ))}
      </div>
    </div>
  );
}

/**
 * Draggable row for a playlist item. Only premium/admin users get a drag
 * handle and drop targeting; standard users see the row with no reorder UI.
 */
function PlaylistRow({ v, idx, canReorder, greyed, onDragStart, onDragOver, onDrop, onPlay, onRemove, dragging }) {
  return (
    <div
      data-testid={`pl-row-${v.id}`}
      onDragOver={canReorder ? onDragOver : undefined}
      onDrop={canReorder ? onDrop : undefined}
      className={`group grid grid-cols-[auto_auto_1fr_auto] items-center gap-3 px-2 sm:px-3 py-2 rounded-lg hover:bg-white/5 border border-transparent hover:border-white/10 ${dragging ? "opacity-50" : ""} ${greyed ? "opacity-40 grayscale" : ""}`}
      data-locked={greyed ? "yes" : "no"}
    >
      {canReorder ? (
        <button
          data-testid={`pl-drag-${v.id}`}
          draggable
          onDragStart={onDragStart}
          title="Drag to reorder"
          className="cursor-grab active:cursor-grabbing text-zinc-500 hover:text-zinc-300 w-6 flex items-center justify-center"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      ) : (
        <span className="w-6 text-center text-xs text-zinc-500 font-mono-ui">{idx + 1}</span>
      )}
      <button onClick={onPlay} data-testid={`pl-play-${v.id}`} className="relative w-14 h-14 rounded-md overflow-hidden shrink-0 group/img">
        <img src={v.cover_url} alt="" className="w-full h-full object-cover" />
        <span className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 flex items-center justify-center transition-opacity">
          <Play className="w-5 h-5 text-white ml-0.5" />
        </span>
      </button>
      <div className="min-w-0">
        <div className="font-semibold text-sm truncate flex items-center gap-2">
          {v.title}
          {v.premium && <Sparkles className="w-3 h-3 text-amber-400 shrink-0" />}
          {greyed && <Lock className="w-3 h-3 text-amber-400 shrink-0" />}
        </div>
        <div className="text-xs text-zinc-400 truncate">{v.artist}</div>
      </div>
      <button
        onClick={onRemove}
        data-testid={`pl-remove-${v.id}`}
        title="Remove"
        className="opacity-0 group-hover:opacity-100 touch-show p-1.5 rounded-full hover:bg-white/10 text-zinc-500 hover:text-zinc-200 transition-opacity"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export function PlaylistDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const player = usePlayer();
  const [pl, setPl] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const previousOrderRef = useRef(null);

  const canReorder = !!user && (user.is_premium || user.role === "admin");

  const load = () => api.get(`/playlists/${id}`).then((r) => setPl(r.data));
  useEffect(() => { load(); }, [id]);

  if (!pl) return null;

  // Premium playlists can hold the same song more than once. occurrences[i] says
  // which copy of its song row i is (0 = first copy), so Remove deletes just that one.
  const seenCount = {};
  const occurrences = pl.videos.map((v) => {
    seenCount[v.id] = (seenCount[v.id] ?? -1) + 1;
    return seenCount[v.id];
  });

  const del = async () => {
    if (!confirm("Delete this playlist?")) return;
    await api.delete(`/playlists/${id}`); nav("/playlists");
  };

  const removeItem = async (i) => {
    const v = pl.videos[i];
    try {
      await api.delete(`/playlists/${id}/items/${v.id}`, { params: { occurrence: occurrences[i] } });
      setPl({ ...pl, videos: pl.videos.filter((_, idx) => idx !== i) });
    } catch { toast.error("Failed to remove"); }
  };

  const persistOrder = async (videos) => {
    try {
      await api.post(`/playlists/${id}/reorder`, { video_ids: videos.map((v) => v.id) });
    } catch (err) {
      // Backend returns 403 premium_required for standard users; the global
      // axios interceptor pops the UpgradePrompt modal. Revert local order.
      if (previousOrderRef.current) setPl({ ...pl, videos: previousOrderRef.current });
      if (err?.response?.data?.detail !== "premium_required") toast.error("Reorder failed");
    } finally {
      previousOrderRef.current = null;
    }
  };

  const onDragStart = (i) => (e) => {
    setDragIdx(i);
    previousOrderRef.current = pl.videos;
    try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(i)); } catch { /* ignore */ }
  };
  const onDragOver = (e) => { e.preventDefault(); try { e.dataTransfer.dropEffect = "move"; } catch { /* ignore */ } };
  const onDrop = (targetIdx) => (e) => {
    e.preventDefault();
    const from = dragIdx;
    setDragIdx(null);
    if (from === null || from === targetIdx) return;
    const next = pl.videos.slice();
    const [moved] = next.splice(from, 1);
    next.splice(targetIdx, 0, moved);
    setPl({ ...pl, videos: next });
    persistOrder(next);
  };

  const playable = pl.videos.filter((v) => !v.plan_locked);
  const hasLocked = pl.videos.some((v) => v.plan_locked);
  const playRow = (i) => {
    const v = pl.videos[i];
    if (v.plan_locked) { askForPremium(pl.locked ? "This playlist is saved, but Premium is needed to use it" : "Free accounts can play the first 10 songs. Premium unlocks the rest"); return; }
    player.playQueue(playable, i, { playlist: true });   // locked rows are always the trailing ones, so the position is the same
  };

  return (
    <div>
      {hasLocked && (
        <div data-testid="playlist-locked-note" className="mb-4 rounded-xl border border-amber-400/30 bg-amber-400/5 px-4 py-3 text-sm text-amber-200">
          {pl.locked
            ? "This playlist is saved and nothing is lost, but free accounts can only use their first playlist. Premium unlocks it again."
            : "Your songs are all saved. Free accounts can play the first 10 songs in a playlist, so the rest are greyed out. Premium unlocks them again."}
        </div>
      )}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl font-extrabold">{pl.name}</h1>
          {canReorder && pl.videos.length > 1 && (
            <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1.5">
              <GripVertical className="w-3 h-3" /> Drag the handle to reorder tracks
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button data-testid="delete-playlist-btn" onClick={del} className="p-2 rounded-full hover:bg-white/10 text-zinc-400">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {pl.videos.length > 0 ? (
        <div data-testid="playlist-rows" className="flex flex-col gap-0.5 bg-zinc-900/40 border border-white/10 rounded-xl p-2 sm:p-3">
          {pl.videos.map((v, i) => (
            <PlaylistRow
              key={`${v.id}-${occurrences[i]}`}
              v={v}
              idx={i}
              canReorder={canReorder}
              dragging={dragIdx === i}
              greyed={!!v.plan_locked}
              onDragStart={onDragStart(i)}
              onDragOver={onDragOver}
              onDrop={onDrop(i)}
              onPlay={() => playRow(i)}
              onRemove={() => removeItem(i)}
            />
          ))}
        </div>
      ) : (
        <div className="text-zinc-500 py-20 text-center">Empty — add videos from any video page.</div>
      )}
    </div>
  );
}
